<template>
  <div>
    <el-button :loading="loading"
               icon="el-icon-refresh-right"
               size="mini"
               @click="refresh">
      重新載入
    </el-button>
    <el-button
        :loading="loading"
        icon="el-icon-delete"
        size="mini"
        type="danger"
        @click="handleBatchDelete"
    >
      批次刪除
    </el-button>
    <el-button
        :icon="showFilter ? 'el-icon-remove-outline': 'el-icon-circle-plus-outline'"
        size="mini"
        type="success"
        @click="showFilter = !showFilter"
    >
      過濾器
    </el-button>
    <el-collapse-transition>
      <el-card
          v-if="showFilter"
          shadow="never"
          style="margin: 2px 0;"
      >
        <el-form :model="filter" label-width="120px">
          <el-row>
            <el-col :xl="6">
              <el-form-item label="教室">
                <el-select
                    v-model="filter.roomIds"
                    clearable
                    filterable
                    multiple
                    placeholder="請選擇教室(多選)"
                >
                  <el-option
                      v-for="r in rooms"
                      :key="r.ncutRoomId"
                      :label="r.name + (r.isHidden ? '（已隱藏）' : '')"
                      :value="r.ncutRoomId"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xl="8">
              <el-form-item label="日期起訖">
                <el-date-picker
                    v-model="filter.range"
                    :picker-options="{firstDayOfWeek:1}"
                    end-placeholder="請選擇結束日期"
                    range-separator="到"
                    start-placeholder="請選擇開始日期"
                    type="daterange"
                    value-format="yyyy-MM-dd"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :xl="6">
              <el-form-item label="借用星期">
                <el-select
                    v-model="filter.weekdays"
                    clearable
                    multiple
                    placeholder="請選擇借用星期(多選)"
                >
                  <el-option
                      v-for="w in weekdays"
                      :key="w.key"
                      :label="w.label"
                      :value="w.key"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>

          </el-row>
          <el-row>
            <el-col :xl="12">
              <el-form-item label="借用節次(起訖)">
                <period-picker
                    v-model="filter.periods"
                    :comparer="(b, e) => e >= b"
                    :options="periodOpts"
                    clearable
                    @beginChange="filter.periods[1] = ''"
                ></period-picker>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
    </el-collapse-transition>
    <el-divider v-if="showFilter">分隔線</el-divider>
    <el-table
        ref="unavailableTable"
        :data="pagedData"
        :height="height"
        :stripe="true"
        empty-text="本學期設定"
        @selection-change="handleSelectionChange"
    >
      <el-table-column
          type="selection"
          width="55">
      </el-table-column>
      <el-table-column
          label="教室"
          prop="roomName"
      ></el-table-column>
      <el-table-column
          label="不可借用星期"
      >
        <template #default="{row}">
          {{ weekdays.filter(w => row.weekdays.some(rw => rw === w.key)).map(w => w.label).join(', ') }}
        </template>
      </el-table-column>
      <el-table-column label="不可借用節次">
        <template #default="{row}">
          {{ `[${row.periodBegin}-${row.periodEnd}]` }}
        </template>
      </el-table-column>
      <el-table-column label="不可借用期間">
        <template #default="{row}">
          {{ row.sameAsSemesterRange ? '全學期' : `${row.beginDate} ~ ${row.endDate}` }}
        </template>
      </el-table-column>
      <el-table-column
          label="設定者"
          prop="who"
      ></el-table-column>
      <el-table-column
          label="事由"
          prop="reason"
      ></el-table-column>
      <el-table-column
          label="操作"
      >
        <template #default="{row}">
          <div style="margin-bottom: 5px;">
            <el-button
                icon="el-icon-edit"
                size="mini"
                type="warning"
                @click="select(row)"
            >
              修改
            </el-button>
          </div>
          <div style="margin-bottom: 5px;">
            <el-button
                icon="el-icon-delete"
                size="mini"
                type="danger"
                @click="handleDelete(row.id)"
                title="直接刪除"
            ></el-button>
          </div>

        </template>
      </el-table-column>
    </el-table>
    <el-pagination
        :current-page.sync="currentPage"
        :page-size="pageSize"
        :total="filteredUnavailables.length"
        background
        layout="total, prev, pager, next"
        style="margin-top: 10px;"
        @current-change="page => pageChange(page, filteredUnavailables)"
    ></el-pagination>
    <el-dialog
        :visible.sync="show"
        @closed="handleDialogClosed"
    >
      <template #title>
        異動(請選擇新的設定)
      </template>
      <unavailable-form
          ref="formRef"
          :selected="selected"
          :with-room="false"
      ></unavailable-form>
      <template #footer>
        <el-button
            :loading="loading"
            type="success"
            @click="submit"
        >
          異動
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import moment from 'moment';
import PagingMixin from '../../pagingMixin';
import UnavailableForm from './UnavailableForm';
import UnavailableMixin from './UnavailableMixin';
import PeriodPicker from '../PeriodPicker';

const defaultFilter = {
  roomIds: [],
  range: undefined,
  weekdays: [],
  periods: []
};

export default {
  name: 'UnavailableDatatable',
  data: () => ({
    pageSize: 10,
    showFilter: false,
    filter: {...defaultFilter},
    selection: [],
    show: false,
    selected: undefined
  }),
  components: {UnavailableForm, PeriodPicker},
  computed: {
    ...mapState('unavailable', ['unavailables']),
    filteredUnavailables() {
      const {roomIds, range, weekdays, periods} = this.filter;
      return this
          .unavailablesWithRoomName
          .filter(e =>
              roomIds.length === 0 || roomIds.some(r => r === e.roomId))
          .filter(e =>
              (!range || moment(e.beginDate)
                      .isBetween(range[0], range[1], 'd', '[]')
                  || moment(e.endDate)
                      .isBetween(range[0], range[1], 'd', '[]')
              )
          )
          .filter(e => weekdays.length === 0 || e.weekdays.some(e => weekdays.some(w => w === e)))
          .filter(e => (!periods[0] || !periods[1]) || e.periodBegin >= periods[0] && e.periodEnd <= periods[1])
          ;
    },
    unavailablesWithRoomName() {
      return this.unavailables
          .map(u => {
            const room = this.rooms.find(r => r.ncutRoomId === u.roomId) || {name: ''};
            return {
              ...u,
              roomName: room.name + (room.isHidden ? '（已隱藏）' : '')
            };
          });
    },

    height() {
      return '620px';
      // if (this.filteredUnavailables.length <= this.pageSize) {
      //   return `${62 * this.filteredUnavailables.length}px`;
      // } else {
      //   return '620px';
      // }
    },
  },
  mixins: [PagingMixin, UnavailableMixin],
  methods: {
    ...mapActions('unavailable', {
      getUnavailables: 'getUnavailablesAction',
      deleteUnavailableRange: 'deleteUnavailableRangeAction',
      deleteUnavailable: 'deleteUnavailableAction',
      updateUnavailable: 'updateUnavailableAction',
    }),

    async refresh() {
      await this.getUnavailables({
        all: true,
        conditions: {
          schoolYear: this.schoolYear,
          semester: this.semester
        },
      });
      this.clearFilter();
    },

    clearFilter() {
      this.filter = {...defaultFilter};
    },

    handleSelectionChange(val) {
      this.selection = val;
    },

    async handleDelete(id) {
      try {
        await this.$confirm('確定要刪除嗎?', {
          type: 'warning',
        });
        const {success, messages} = await this.deleteUnavailable(id);
        if (success) {
          this.$message.success('異動成功');
        } else {
          this.$message.warning('異動失敗，請重試一次');
          if (messages) {
            this.$message.warning(messages.join(','));
          }
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message({
            type: 'error',
            message: '發生錯誤，請重試一次!'
          });
        }
      }
    },
    async handleBatchDelete() {
      if (this.selection.length <= 0) {
        this.$message({
          message: '請選擇至少一個設定',
          type: 'warning'
        });
        return;
      }
      try {
        await this.$confirm('確定要刪除嗎?', {
          type: 'warning',
        });
        const {success, messages} = await
            this.deleteUnavailableRange(this.selection.map(s => s.id));
        this.selection = [];
        this.$refs.unavailableTable.clearSelection();
        if (success) {
          this.$message.success('異動成功');
        } else {
          this.$message.warning('異動失敗，請重試一次');
          if (messages) {
            this.$message.warning(messages.join(','));
          }
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message({
            type: 'error',
            message: '發生錯誤，請重試一次!'
          });
        }
      }
    },

    select(unavailable) {
      this.show = true;
      this.selected = {...unavailable};
    },

    handleDialogClosed() {
      const {formRef} = this.$refs;
      if (formRef) {
        formRef.reset();
        this.selected = undefined;
      }
    },

    async submit() {
      const {getFields, getRef, reset, isSelectCollided} = this.$refs.formRef;
      const formRef = getRef();
      const form = getFields();
      await formRef.validate(async (valid) => {
        if (valid) {
          const {schoolYear, semester, selected} = this;
          const unavailable = {
            roomId: selected.roomId,
            beginDate: form.range[0],
            endDate: form.range[1],
            periodBegin: form.periods[0],
            periodEnd: form.periods[1],
            weekdays: [...form.weekdays],
            schoolYear,
            semester,
            sameAsSemesterRange: form.sameAsSemesterRange,
            id: selected.id,
            reason: form.reason,
            who: this.name
          };

          if (isSelectCollided()) {
            this.$message({
              type: 'warning',
              message: '您選的設定，目前有衝堂，請選擇別的設定'
            });
            return;
          }
          const {success, messages = []} = await this.updateUnavailable(unavailable);
          if (success) {
            this.$message.success('更新成功');
            if (reset && typeof reset === 'function') {
              reset();
              this.show = false;
            }
          } else {
            this.$message.warning(messages.join(','));
          }
        } else {
          this.$message({
            type: 'warning',
            message: '您還有欄位尚未輸入!!'
          });
        }
      });
    },
  }
  ,
  watch: {
    filteredUnavailables(current) {
      this.pagedData = this.pageData(current, 1);
      this.currentPage = 1;
    },

    async schoolYear(current) {
      if (current) {
        await this.getUnavailables({
          all: true,
          conditions: {
            schoolYear: this.schoolYear,
            semester: this.semester
          },
        });
      }
    },

  },
  async created() {
    const {schoolYear, semester} = this;
    if (schoolYear) {
      await this.getUnavailables({
        all: true,
        conditions: {
          schoolYear: schoolYear,
          semester: semester
        }
      });
    }
  }
}
</script>

<style>

</style>
